import {
	AnonymousStudentProfileUpdated,
	AnonymousStudentServiceReady,
	IAnonymousStudentService
} from '@studyportals/anonymous-student-interfaces';
import { DataStorage } from '@studyportals/data-storage';

import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { StudentField } from '@studyportals/studentdomain';
import { CurrencyConversionServiceReadyEvent, ICurrencyConversionService } from '../interface';
import { DiscountCalculator } from '@studyportals/iu-global-access-initiative/DiscountCalculator';
import { DiscountInformation } from '@studyportals/iu-global-access-initiative/interfaces/DiscountInformation';
import { CountryTier } from '@studyportals/iu-global-access-initiative/definitions/CountryTier';
import { IUDiscountCriteria } from './IUDiscountCriteria';
import { DiscountCriteria } from '@studyportals/iu-global-access-initiative/interfaces/DiscountCriteria';

export class TuitionFeeDiscountService 
implements ISubscriber<AnonymousStudentServiceReady | CurrencyConversionServiceReadyEvent>
{
	private anonymousStudent?: IAnonymousStudentService;
	private currencyConversionService?: ICurrencyConversionService;
	private discountCalculator = new DiscountCalculator();

	private partnershipUniversities = ['421', '21013', '21012'];

	constructor() {
		const organisationInformation = document.querySelector('.TrackingLocation.TrackingEvent');
		if (organisationInformation) {
			const organisationId = organisationInformation.getAttribute('data-organisation-id');
			if (organisationId && this.partnershipUniversities.includes(organisationId)) {
				window.EventAggregationService.subscribeTo(AnonymousStudentServiceReady.EventType, this, true);
				window.EventAggregationService.subscribeTo(AnonymousStudentProfileUpdated.EventType, this, false);
				window.EventAggregationService.subscribeTo(CurrencyConversionServiceReadyEvent.EventType, this, true);
			}
		}
	}

	public async notify(event: AnonymousStudentServiceReady | CurrencyConversionServiceReadyEvent): Promise<void> {
		if (event.eventType === AnonymousStudentServiceReady.EventType) {
			this.anonymousStudent = (event as AnonymousStudentServiceReady).anonymousStudentService;
		}

		if (event.eventType === CurrencyConversionServiceReadyEvent.EventType) {
			this.currencyConversionService = (event as CurrencyConversionServiceReadyEvent).currencyConversionService;
		}

		if (this.anonymousStudent && this.currencyConversionService) {
			await this.update();
			await this.currencyConversionService.update();
			this.listenForGlobalAccessInitiativeQuestionnaireTaken();
			document.dispatchEvent(new CustomEvent('fee_discount_applied'));
		}
	}

	public async update(): Promise<void> {
		const countryId = await this.getCountryOfResidence();
		const discountInformation: DiscountInformation = this.getDiscountInformation(countryId);
		if (discountInformation == null) {
			return;
		}

		if (this.shouldRemoveScholarshipIncentive(discountInformation.countryTier)) {
			this.removeScholarshipTuitionFeeElements();
			this.replaceScholarshipIncentiveLabelElementWithText();
			return;
		}

		this.manageDiscountInformation(discountInformation);
	}

	private manageDiscountInformation(discountInformation: DiscountInformation): void {
		if (discountInformation.percentage === 0) {
			this.removeScholarshipTuitionFeeElements();
			return;
		}

		if (discountInformation.priceInformation) {
			this.overrideBasePriceOriginal(discountInformation.priceInformation.basePrice.toString());
			this.overrideBasePrice(discountInformation.priceInformation.basePricePerUnit.toString());
			this.applyDiscount(discountInformation.priceInformation.discountPricePerUnit.toString());
		}

		this.setDiscountTooltip(discountInformation.percentage);
	}

	private overrideBasePriceOriginal(basePrice: string): void {
		const elementsWithOriginalAmountData = document.querySelectorAll('[data-original-amount]');

		for (const element of elementsWithOriginalAmountData) {
			element.setAttribute('data-original-amount', basePrice);
		}

		const basePriceOriginalElements = document.querySelectorAll('.js-base-price-original');
		for (const basePriceOriginalElement of basePriceOriginalElements) {
			basePriceOriginalElement.innerHTML = basePrice;
		}
	}

	private overrideBasePrice(finalPrice: string): void {
		const basePriceElements = document.querySelectorAll('.js-base-price');

		for (const basePriceElement of basePriceElements) {
			basePriceElement.setAttribute('data-amount', finalPrice);
			basePriceElement.innerHTML = finalPrice;
		}
	}

	private applyDiscount(finalFeePerUnit: string): void {
		const discountElements = document.querySelectorAll('[data-discount]');

		for (const discountElement of discountElements) {
			discountElement.setAttribute('data-amount', finalFeePerUnit);
		}
	}

	private replaceScholarshipIncentiveLabelElementWithText(): void {
		const scholarshipIncentiveLabel = document.querySelector('.ScholarshipIncentiveLabel');
		if (!scholarshipIncentiveLabel || !scholarshipIncentiveLabel.parentNode) {
			return;
		}

		scholarshipIncentiveLabel.parentNode.replaceChild(
			document.createTextNode('Tuition fee'),
			scholarshipIncentiveLabel
		);
	}

	private removeScholarshipTuitionFeeElements(): void {
		const originalTuitionFeeValueContainers = document.querySelectorAll('.js-scholarshipTuitionFeeWrapper');
		for (const container of originalTuitionFeeValueContainers) {
			container.remove();
		}
	}

	private setDiscountTooltip(percentage: number): void {
		const tooltip = this.getTooltipContent(percentage);

		const scholarshipInfoIconElements = document.querySelectorAll('.ScholarshipCurrencyWrapper .InfoIcon');
		for (const infoIcon of scholarshipInfoIconElements) {
			infoIcon.setAttribute('data-tooltip-text', tooltip);
		}

		document.dispatchEvent(new CustomEvent('render_complete'));
	}

	private async getCountryOfResidence(): Promise<number | undefined> {
		if (!this.anonymousStudent) {
			return;
		}

		const data = await this.anonymousStudent.getStudentData([
			StudentField.RESIDENCE_COUNTRY_ID,
			StudentField.NATIONALITY_COUNTRY_ID
		]);

		const residenceCountryId = data[StudentField.RESIDENCE_COUNTRY_ID];
		if (residenceCountryId) {
			return residenceCountryId;
		}
		const nationalityCountryId = data[StudentField.NATIONALITY_COUNTRY_ID];
		if (nationalityCountryId) {
			return nationalityCountryId;
		}

		return undefined;
	}

	private getDiscountInformation(countryId?: number): DiscountInformation {
		if (!countryId) {
			return {
				percentage: 0,
				countryTier: CountryTier.INELIGIBLE
			} as DiscountInformation;
		}

		const discountCriteria = this.getDiscountCriteria(countryId);

		return this.discountCalculator.getDiscountPercentage(discountCriteria);
	}

	private getDiscountCriteria(countryId: number): DiscountCriteria {
		return new IUDiscountCriteria(countryId).get();
	}

	private getTooltipContent(discountInPercentage: number): string {
		if (this.isGlobalAccessInitiativeQuestionnaireTaken()) {
			return `${discountInPercentage}% tuition fee reduction unlocked on the programme website!`;
		}

		return `${discountInPercentage}% tuition fee reduction available for you!`;
	}

	private isGlobalAccessInitiativeQuestionnaireTaken(): boolean {
		const dataStorageKey = 'global_access_initiative_questionnaire_taken';
		return DataStorage.retrieve(dataStorageKey) === 'true';
	}

	private shouldRemoveScholarshipIncentive(countryTier: CountryTier): boolean {
		return this.isGlobalAccessInitiativeQuestionnaireTaken() && countryTier === CountryTier.INELIGIBLE;
	}

	private listenForGlobalAccessInitiativeQuestionnaireTaken(): void {
		document.addEventListener('global_access_initiative_questionnaire_taken', () => {
			void (async (): Promise<void> => {
				await this.update();
				await this.currencyConversionService?.update();
			})();
		});
	}
}
